import type { ReactNode } from 'react'

/**
 * The card that sits above a list and adds one more thing to it.
 *
 * Three screens have this shape — sending domains, the team, API keys — and
 * each was carrying its own copy of the wrapper and the submit button. The copy
 * is the problem rather than the length: the button's disabled state is where
 * "do not let somebody submit an empty form twice" lives, and three spellings
 * of it is two that will drift the first time one of them is fixed.
 *
 * The fields stay with the screen. What they are is the screen's business; the
 * frame around them is not.
 */
export function CreateCard({
  heading,
  submit,
  disabled,
  onSubmit,
  children,
}: {
  /** Omitted where the page heading already says what the form is for. */
  readonly heading?: string
  readonly submit: string
  /**
   * Disables the button and nothing else.
   *
   * The fields carry their own disabled state, because a screen mid-request
   * usually wants them readable rather than greyed out.
   */
  readonly disabled: boolean
  readonly onSubmit: () => void
  readonly children: ReactNode
}) {
  return (
    <form
      className="mb-6 max-w-2xl rounded-card border border-line bg-surface p-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      {heading === undefined ? null : (
        <h2 className="mb-4 font-bold text-[15px] text-t1">{heading}</h2>
      )}
      {children}
      <button
        type="submit"
        disabled={disabled}
        className="mt-6 rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submit}
      </button>
    </form>
  )
}
